import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, getDocs, query, where, orderBy, doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoCheckmarkCircleOutline, IoLockClosed, IoStatsChartOutline } from 'react-icons/io5';
import './EncuestasScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

function EncuestasScreen() {
    const navigate = useNavigate();
    const { userData, currentUser, isMoroso } = useAuth();
    const [encuestas, setEncuestas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [votosRegistrados, setVotosRegistrados] = useState({}); // { encuestaId: opcionElegida (o true si anónimo) }
    const [procesando, setProcesando] = useState(false);

    useEffect(() => {
        if (!userData?.condominioId || !currentUser) return;
        fetchEncuestas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, currentUser]);

    const fetchEncuestas = async () => {
        setLoading(true);
        try {
            // 1. Obtener encuestas
            const q = query(
                collection(db, 'encuestas'),
                where('condominioId', '==', userData.condominioId),
                orderBy('fechaInicio', 'desc')
            );
            const snapshot = await getDocs(q);
            const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // 2. Revisar cuáles ya votó el usuario
            let votosMap = {};
            for (let enc of list) {
                const votoDoc = await getDoc(doc(db, 'encuestas', enc.id, 'votos', currentUser.uid));
                if (votoDoc.exists()) {
                    votosMap[enc.id] = votoDoc.data().opcionId || true;
                }
            }

            setVotosRegistrados(votosMap);
            setEncuestas(list);
        } catch (error) {
            console.error("Error cargando encuestas", error);
        }
        setLoading(false);
    };

    const handleVotar = async (encuestaId, opcionId, esAnonima, opcionesLocales) => {
        if (procesando) return;
        if (!window.confirm("¿Seguro que deseas enviar este voto? No podrás deshacerlo.")) return;

        setProcesando(true);
        try {
            // Referencias
            const encuestaRef = doc(db, 'encuestas', encuestaId);
            const votoRef = doc(encuestaRef, 'votos', currentUser.uid);

            // Validar de nuevo si el voto ya existe (por seguridad)
            const checkVoto = await getDoc(votoRef);
            if (checkVoto.exists()) {
                alert("Ya has votado en esta encuesta.");
                setProcesando(false);
                return;
            }

            // Registrar Voto en la subcolección
            await setDoc(votoRef, {
                numeroCasa: esAnonima ? 'Anónimo' : (userData.numeroCasa || 'N/A'),
                rolVotante: userData.tipoResidente || 'Inquilino',
                opcionId: esAnonima ? null : opcionId, // Si es anónima no vinculamos el ID de la opción al usuario
                fecha: new Date().toISOString()
            });

            // Incrementar contador en el documento principal
            // Buscamos el índice y reconstruimos las opciones
            const nuevasOpciones = opcionesLocales.map(opt => {
                if (opt.id === opcionId) {
                    return { ...opt, votos: opt.votos + 1 };
                }
                return opt;
            });

            await updateDoc(encuestaRef, {
                opciones: nuevasOpciones,
                totalVotos: increment(1)
            });

            alert("¡Voto registrado con éxito!");
            // Actualizar estado local
            setVotosRegistrados(prev => ({ ...prev, [encuestaId]: esAnonima ? true : opcionId }));
            setEncuestas(encuestas.map(enc => enc.id === encuestaId ? { ...enc, opciones: nuevasOpciones, totalVotos: (enc.totalVotos || 0) + 1 } : enc));
        } catch (error) {
            console.error("Error al votar", error);
            alert("Hubo un error al registrar tu voto.");
        }
        setProcesando(false);
    };

    const isEligibleToVote = (encuesta) => {
        const { reglas, estado } = encuesta;
        if (estado === 'cerrada') return { eligible: false, reason: 'La encuesta ha finalizado' };
        if (reglas.soloPropietarios && userData.tipoResidente !== 'Propietario') {
            return { eligible: false, reason: 'Solo propietarios pueden votar en esta encuesta' };
        }
        if (!reglas.permiteMorosos && isMoroso) {
            return { eligible: false, reason: 'Necesitas estar al corriente con tus pagos para participar' };
        }
        if (votosRegistrados[encuesta.id]) {
            return { eligible: false, reason: 'Ya has emitido tu voto' };
        }
        return { eligible: true, reason: '' };
    };

    const calcularPorcentaje = (votosOpcion, totalVotos) => {
        if (!totalVotos || totalVotos === 0) return 0;
        return Math.round((votosOpcion / totalVotos) * 100);
    };

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/encuestas" />
            <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>
        <div className="ks-scroll-area">
          <div className="muro-header-stitch">
            <div className="muro-hero-card">
              <div className="muro-hero-content">
                <h1>Votaciones</h1>
                <p>Participación vecinal</p>
              </div>
              <IoStatsChartOutline className="muro-hero-icon-decor" />
            </div>
          </div>
          <div className="encuestas-container">

            <div style={{ padding: '20px' }}>
                {loading ? <p style={{ textAlign: 'center' }}>Cargando encuestas...</p> : encuestas.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#6b7280', marginTop: '40px' }}>
                        <IoStatsChartOutline size={50} />
                        <p>No hay encuestas activas en tu condominio.</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '20px' }}>
                        {encuestas.map(enc => {
                            const { eligible, reason } = isEligibleToVote(enc);
                            const yaVoto = votosRegistrados[enc.id] !== undefined;
                            const mostrarResultadosPublicos = enc.reglas.resultadosPublicos === 'siempre' ||
                                (enc.reglas.resultadosPublicos === 'despues_de_votar' && yaVoto) ||
                                enc.estado === 'cerrada';

                            // Si es calificación o si/no se puede ajustar la vista, pero los botones base sirven.
                            return (
                                <div key={enc.id} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', position: 'relative', overflow: 'hidden' }}>

                                    {enc.estado === 'cerrada' && (
                                        <div style={{ position: 'absolute', top: 15, right: -30, backgroundColor: '#ef4444', color: 'white', padding: '5px 40px', transform: 'rotate(45deg)', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                            CERRADA
                                        </div>
                                    )}

                                    <h3 style={{ margin: '0 0 10px 0', color: '#1f2937', paddingRight: '40px' }}>{enc.titulo}</h3>
                                    {enc.descripcion && <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '15px' }}>{enc.descripcion}</p>}

                                    <div style={{ display: 'flex', gap: '10px', fontSize: '0.8rem', color: '#8b5cf6', marginBottom: '20px', flexWrap: 'wrap' }}>
                                        {enc.reglas.esAnonima && <span style={{ padding: '4px 8px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>Voto Anónimo</span>}
                                        {enc.reglas.soloPropietarios && <span style={{ padding: '4px 8px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>Solo Propietarios</span>}
                                        {enc.totalVotos !== undefined && <span style={{ padding: '4px 8px', backgroundColor: '#e0e7ff', borderRadius: '6px' }}>{enc.totalVotos} Votos Emitidos</span>}
                                    </div>

                                    {!eligible && !yaVoto && (
                                        <div style={{ backgroundColor: '#fee2e2', color: '#b91c1c', padding: '10px', borderRadius: '8px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' }}>
                                            <IoLockClosed size={20} />
                                            <span>{reason}</span>
                                        </div>
                                    )}

                                    {yaVoto && (
                                        <div style={{ backgroundColor: '#dcfce7', color: '#166534', padding: '10px', borderRadius: '8px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                            <IoCheckmarkCircleOutline size={22} />
                                            <span>Ya has emitido tu voto.</span>
                                        </div>
                                    )}

                                    {/* OPCIONES DE VOTO */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {enc.opciones.map(opt => {
                                            const porcentaje = calcularPorcentaje(opt.votos, enc.totalVotos);
                                            const fueMiVoto = votosRegistrados[enc.id] === opt.id;

                                            return (
                                                <div key={opt.id} style={{ position: 'relative' }}>
                                                    {mostrarResultadosPublicos && (
                                                        <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: porcentaje + '%', backgroundColor: fueMiVoto ? '#c4b5fd' : '#f3f4f6', borderRadius: '8px', zIndex: 0, transition: 'width 0.5s ease-out' }} />
                                                    )}
                                                    <button
                                                        disabled={!eligible || procesando}
                                                        onClick={() => handleVotar(enc.id, opt.id, enc.reglas.esAnonima, enc.opciones)}
                                                        style={{
                                                            width: '100%', padding: '15px', textAlign: 'left',
                                                            backgroundColor: 'transparent', border: fueMiVoto ? '2px solid #8b5cf6' : '1px solid #d1d5db',
                                                            borderRadius: '8px', display: 'flex', justifyContent: 'space-between',
                                                            position: 'relative', zIndex: 1,
                                                            cursor: eligible && !procesando ? 'pointer' : 'default',
                                                            color: fueMiVoto ? '#6d28d9' : '#1f2937',
                                                            fontWeight: fueMiVoto ? 'bold' : 'normal',
                                                            opacity: (!eligible && !yaVoto) ? 0.6 : 1
                                                        }}
                                                    >
                                                        <span>{opt.texto}</span>
                                                        {mostrarResultadosPublicos && (
                                                            <span>{porcentaje}% ({opt.votos})</span>
                                                        )}
                                                    </button>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
              </div>
            </div>
          </div>
          {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
          <KondoBottomNav activeRoute="/encuestas" />
        </main>
      </div>
    );
}

export default EncuestasScreen;
